import React, { useState, useEffect } from 'react';
import { BadgeCheck, ShieldAlert, ShieldCheck, Users, CalendarClock, History, Loader2, MessageSquarePlus, ArrowRightLeft, UserCheck, PhoneCall, CircleDollarSign, FileWarning } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { logAction, logUsageEvent } from '../lib/logger';
import {
    CobrancaCliente, CobrancaStatusNegociacao,
    CobrancaCanalContato, CobrancaResultadoContato, EventoTimelineCobranca, CobrancaFonteEvento,
} from '../../types';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { ensureCobrancaCliente } from '../lib/cobrancaCliente';
import {
    atualizarStatusCobrancaCliente,
    adicionarObservacaoCobranca,
    transferirCobrancaCliente,
    fetchResponsaveisCobrancaElegiveis,
    fetchTimelineCobrancaCliente,
    fetchResponsaveisPorEtapa,
} from '../services/api';
import { registrarContatoCliente, registrarRetiradaNegativacao } from '../services/esteiraCobranca';
import DesfechoContatoCampos from './DesfechoContatoCampos';
import {
    avisoDoDesfecho, desfechoInicial, diaSeguinte, faltaNoDesfecho, type CamposDesfecho,
} from '../lib/desfechoContato';

/** 'YYYY-MM-DD' de hoje (ou de hoje + n dias) no fuso de quem usa a tela. */
const diaLocalISO = (deslocamentoDias = 0): string => {
    const d = new Date(Date.now() + deslocamentoDias * 86_400_000);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
};

export const STATUS_NEGOCIACAO_META: Record<CobrancaStatusNegociacao, { label: string; classes: string }> = {
    aguardando_contato: { label: 'Aguardando contato', classes: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
    negociando: { label: 'Negociando', classes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    negociado: { label: 'Negociado', classes: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
    pago: { label: 'Pago', classes: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
    notificacao_extrajudicial: { label: 'Notificação extrajudicial', classes: 'bg-orange-200 text-orange-900 dark:bg-orange-900/50 dark:text-orange-300' },
    notificacao_realizada: { label: 'Notificação realizada', classes: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    sem_pagamento: { label: 'Sem pagamento', classes: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
    negativacao_pendente: { label: 'Negativação pendente', classes: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    negativacao_realizada: { label: 'Negativação realizada', classes: 'bg-red-200 text-red-900 dark:bg-red-900/50 dark:text-red-300' },
};

/**
 * Rótulo e cor de cada origem da timeline consolidada. O histórico de cobrança
 * de um cliente vive em cinco tabelas (ver `timeline_cobranca_cliente`,
 * migration 20260825130000); aqui ele vira uma lista só, e a etiqueta é o que
 * diz de onde cada linha veio.
 */
export const FONTE_EVENTO_META: Record<CobrancaFonteEvento, { label: string; classes: string }> = {
    negociacao:  { label: 'Negociação',   classes: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
    contato:     { label: 'Contato',      classes: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
    lembrete:    { label: 'Aviso auto.',  classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
    acao_manual: { label: 'Régua humana', classes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    notificacao: { label: 'Extrajudicial',classes: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    comunicado:  { label: 'Advertência',  classes: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
};

const CANAIS: { v: CobrancaCanalContato; t: string }[] = [
    { v: 'ligacao', t: 'Ligação' },
    { v: 'whatsapp', t: 'WhatsApp' },
    { v: 'email', t: 'E-mail' },
    { v: 'presencial', t: 'Presencial' },
    { v: 'carta', t: 'Carta / AR' },
    { v: 'outro', t: 'Outro' },
];

const RESULTADOS: { v: CobrancaResultadoContato; t: string }[] = [
    { v: 'nao_atendeu', t: 'Não atendeu' },
    { v: 'sem_retorno', t: 'Sem retorno' },
    { v: 'atendeu', t: 'Atendeu' },
    { v: 'prometeu_pagar', t: 'Prometeu pagar' },
    { v: 'recusou_negociar', t: 'Recusou negociar' },
    { v: 'contato_invalido', t: 'Contato inválido' },
    { v: 'outro', t: 'Outro' },
];

interface PainelNegociacaoCobrancaProps {
    isOpen: boolean;
    cpfCnpjCliente: string | null;
    clienteNome?: string | null;
}

/**
 * Painel de gerenciamento de cobrança por cliente (status de negociação,
 * follow-up, negativação, transferência e timeline) — reaproveitado tanto
 * embutido no CobrancaEnviadaModal (Financeiro) quanto dentro do
 * ClienteCobrancaModal (Central de Inadimplência), "um só lugar" para as duas
 * portas de entrada.
 */
const PainelNegociacaoCobranca: React.FC<PainelNegociacaoCobrancaProps> = ({ isOpen, cpfCnpjCliente, clienteNome }) => {
    const { addToast } = useToast();
    const { user, profile } = useAuth();

    const [cobrancaCliente, setCobrancaCliente] = useState<CobrancaCliente | null>(null);
    const [timeline, setTimeline] = useState<EventoTimelineCobranca[]>([]);
    const [showContatoForm, setShowContatoForm] = useState(false);
    const [contatoCanal, setContatoCanal] = useState<CobrancaCanalContato>('ligacao');
    const [contatoResultado, setContatoResultado] = useState<CobrancaResultadoContato>('nao_atendeu');
    const [contatoObs, setContatoObs] = useState('');
    const [contatoData, setContatoData] = useState('');
    // Como ficou o cliente depois do contato: retorno ou status (#6165).
    const [contatoDesfecho, setContatoDesfecho] = useState<CamposDesfecho>(() => desfechoInicial(null, diaLocalISO()));
    const [registrandoContato, setRegistrandoContato] = useState(false);
    // Quem vai negativar no SPC — por padrão a primeira pessoa do D+24
    // ("Conferência e negativação"), senão a do D+17. É o "repassar para a
    // Bruna" do pedido #6147: marcar para negativar passa o cliente para ela.
    const [negativarPara, setNegativarPara] = useState('');
    const [quemNegativaPadrao, setQuemNegativaPadrao] = useState('');
    // Quem faz a notificação extrajudicial — a primeira pessoa do D+17 (hoje a
    // Bruna). Encaminhar passa o cliente só para ela (pedido de 07/10/2026).
    const [showNotificacaoConfirmation, setShowNotificacaoConfirmation] = useState(false);
    const [notificarPara, setNotificarPara] = useState('');
    const [quemNotificaPadrao, setQuemNotificaPadrao] = useState('');
    const [registrandoRetirada, setRegistrandoRetirada] = useState(false);
    const [loadingCobranca, setLoadingCobranca] = useState(true);
    const [analistasCobranca, setAnalistasCobranca] = useState<{ id: string; full_name: string }[]>([]);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [showNegociarForm, setShowNegociarForm] = useState(false);
    const [negociarValor, setNegociarValor] = useState('');
    const [negociarData, setNegociarData] = useState('');
    const [novaObservacao, setNovaObservacao] = useState('');
    const [showPagoForm, setShowPagoForm] = useState(false);
    const [pagoValor, setPagoValor] = useState('');
    const [pagoData, setPagoData] = useState('');
    const [pagoObs, setPagoObs] = useState('');
    const [showNegativarConfirmation, setShowNegativarConfirmation] = useState(false);
    const [showNegativacaoRealizadaConfirmation, setShowNegativacaoRealizadaConfirmation] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferTargetId, setTransferTargetId] = useState('');
    const [transferring, setTransferring] = useState(false);
    const [proximoContato, setProximoContato] = useState('');
    const [showAssumirResponsabilidade, setShowAssumirResponsabilidade] = useState(false);
    const [assumindo, setAssumindo] = useState(false);
    // Nome de exibição do responsável atual — resolvido à parte de
    // analistasCobranca porque quem assume/é assumido pode ser adm/gerente,
    // que não está no pool de analistas com a permissão de cobrança.
    const [responsavelNome, setResponsavelNome] = useState<string | null>(null);

    // Painel de negociação é uma ferramenta de trabalho do time de cobrança —
    // vendedor, parceiro e demais papéis que só acessam essas telas para
    // consulta não devem ver (nem disparar a criação de) dados de negociação.
    const podeVerPainelCobranca = profile?.role === 'analista' || profile?.role === 'adm' || profile?.role === 'gerente';

    /**
     * Recarrega o histórico consolidado. A timeline é buscada por CPF/CNPJ (e
     * não pelo id da linha de cobrança) porque une fontes chaveadas de formas
     * diferentes — UC normalizada, uc_id e o próprio documento.
     */
    const recarregarTimeline = async () => {
        if (!cpfCnpjCliente) return;
        try {
            setTimeline(await fetchTimelineCobrancaCliente(cpfCnpjCliente));
        } catch (error) {
            // Timeline é consulta, não bloqueia o trabalho de cobrança em si.
            console.error('Erro ao carregar histórico de cobrança:', error);
        }
    };

    useEffect(() => {
        const loadCobrancaCliente = async () => {
            if (!isOpen || !cpfCnpjCliente || !podeVerPainelCobranca) return;
            setLoadingCobranca(true);
            try {
                // Cliente que entrou nessa esteira antes desta funcionalidade existir
                // ainda não tem linha em cobranca_clientes — cria na hora (lazy).
                const cliente = await ensureCobrancaCliente(cpfCnpjCliente, clienteNome);
                setCobrancaCliente(cliente);
                setProximoContato(cliente?.proximo_contato_em || '');
                if (cliente) {
                    await recarregarTimeline();
                }

                // Resolve o nome do responsável ANTES de decidir se mostra o
                // pop-up, pra já poder exibi-lo na mensagem ("já está com Fulano").
                if (cliente?.responsavel_cobranca_id) {
                    const { data: responsavelProfile } = await supabase
                        .from('profiles')
                        .select('full_name')
                        .eq('id', cliente.responsavel_cobranca_id)
                        .maybeSingle();
                    setResponsavelNome(responsavelProfile?.full_name || null);
                    // Cliente já tem dono — só pergunta se quer assumir quando NÃO
                    // for o próprio usuário logado (evita perguntar pra quem já é).
                    if (cliente.responsavel_cobranca_id !== user?.id) {
                        setShowAssumirResponsabilidade(true);
                    }
                } else {
                    setResponsavelNome(null);
                    // Cliente sem ninguém atribuído — pergunta na hora se quem abriu
                    // vai assumir, evita clientes "órfãos" esperando alguém notar.
                    if (cliente) {
                        setShowAssumirResponsabilidade(true);
                    }
                }

                const [analistas, pools] = await Promise.all([
                    fetchResponsaveisCobrancaElegiveis(),
                    fetchResponsaveisPorEtapa().catch(() => null),
                ]);
                setAnalistasCobranca(analistas);
                setQuemNegativaPadrao(pools?.['D+24']?.[0] || pools?.['D+17']?.[0] || '');
                setQuemNotificaPadrao(pools?.['D+17']?.[0] || '');
            } catch (error) {
                console.error('Error loading cobranca cliente:', error);
            } finally {
                setLoadingCobranca(false);
            }
        };

        loadCobrancaCliente();
    }, [isOpen, cpfCnpjCliente, podeVerPainelCobranca, user?.id]);

    // Transferir é aberto a qualquer analista/gerente/adm — não só ao dono atual.
    const canTransferirCliente = (): boolean => podeVerPainelCobranca;

    const handleMudarStatus = async (
        statusNovo: CobrancaStatusNegociacao,
        camposExtras?: Partial<Pick<CobrancaCliente,
            | 'valor_negociado' | 'data_pagamento_negociado'
            | 'negativado_em' | 'negativado_por_id' | 'negativado_por_nome'
            | 'negativacao_realizada_em' | 'negativacao_realizada_por_id' | 'negativacao_realizada_por_nome'
            | 'pago_em' | 'pago_por_id' | 'pago_por_nome' | 'valor_pago'
            | 'proximo_contato_em' | 'responsavel_cobranca_id'
            | 'notificacao_encaminhada_em' | 'notificacao_encaminhada_por_id' | 'notificacao_encaminhada_por_nome'
        >>,
        observacao?: string
    ): Promise<boolean> => {
        if (!cobrancaCliente) return false;
        setStatusUpdating(true);
        try {
            const atualizado = await atualizarStatusCobrancaCliente({
                cobrancaClienteId: cobrancaCliente.id,
                statusAnterior: cobrancaCliente.status_negociacao,
                statusNovo,
                userId: user?.id || '',
                userNome: profile?.full_name || user?.email || 'Sistema',
                observacao,
                camposExtras,
            });
            setCobrancaCliente(atualizado);
            setProximoContato(atualizado.proximo_contato_em || '');
            if (camposExtras?.responsavel_cobranca_id && camposExtras.responsavel_cobranca_id !== cobrancaCliente.responsavel_cobranca_id) {
                setResponsavelNome(analistasCobranca.find(a => a.id === camposExtras.responsavel_cobranca_id)?.full_name
                    || (camposExtras.responsavel_cobranca_id === user?.id ? (profile?.full_name || 'Você') : null));
            }
            await recarregarTimeline();
            addToast(`Status atualizado para "${STATUS_NEGOCIACAO_META[statusNovo].label}".`, 'success');
            return true;
        } catch (error: any) {
            console.error('Error updating cobranca status:', error);
            addToast(error.message || 'Erro ao atualizar status da negociação.', 'error');
            return false;
        } finally {
            setStatusUpdating(false);
        }
    };

    const handleConfirmNegociado = async () => {
        if (!negociarValor || !negociarData) {
            addToast('Informe o valor e a data combinados com o cliente.', 'error');
            return;
        }
        await handleMudarStatus('negociado', {
            valor_negociado: Number(negociarValor),
            data_pagamento_negociado: negociarData,
            // O cliente sai do painel e volta no dia seguinte ao combinado:
            // se pagou, não aparece (não deve mais nada); se não pagou, é
            // a hora de cobrar de novo.
            proximo_contato_em: diaSeguinte(negociarData),
        });
        setShowNegociarForm(false);
        setNegociarValor('');
        setNegociarData('');
    };

    /**
     * Registro manual do recebimento pelo próprio agente de cobrança, depois
     * do comprovante conferido. Grava quem confirmou (pago_por_*) porque a
     * pergunta que o time faz não é só "essa cobrança foi paga?", e sim
     * "quem fechou essa cobrança?" — o responsavel_cobranca_id sozinho não
     * responde, já que o cliente pode ter sido transferido no meio do
     * caminho. A outra porta de entrada do 'pago' (comprovante anexado na
     * fatura, marcarCobrancaComoPaga) grava exatamente os mesmos campos.
     */
    const handleConfirmPago = async () => {
        if (!pagoData) {
            addToast('Informe a data em que o pagamento foi recebido.', 'error');
            return;
        }
        await handleMudarStatus(
            'pago',
            {
                // Meio-dia local: 'yyyy-mm-dd' lido como UTC voltaria um dia em UTC-3.
                pago_em: new Date(`${pagoData}T12:00:00`).toISOString(),
                pago_por_id: user?.id || null,
                pago_por_nome: profile?.full_name || user?.email || 'Sistema',
                valor_pago: pagoValor ? Number(pagoValor) : null,
            },
            pagoObs.trim() || 'Pagamento confirmado pelo agente de cobrança.'
        );
        setShowPagoForm(false);
        setPagoValor('');
        setPagoData('');
        setPagoObs('');
    };

    const handleConfirmNegativar = async () => {
        await handleMudarStatus('negativacao_pendente', {
            negativado_em: new Date().toISOString(),
            negativado_por_id: user?.id || null,
            negativado_por_nome: profile?.full_name || user?.email || 'Sistema',
            // Quem vai negativar vira dono do cliente: some da fila de quem
            // fazia o contato e aparece para quem negativa.
            ...(negativarPara ? { responsavel_cobranca_id: negativarPara } : {}),
        });
        setShowNegativarConfirmation(false);
    };

    /**
     * Encaminha para a notificação extrajudicial (pedido da Dhaiane,
     * 07/10/2026). O cliente passa para quem notifica e sai do contato: o banco
     * fecha as etapas D+4/D+10 dele e o painel o mostra só para essa pessoa,
     * até a notificação ser gerada. O retorno agendado deixa de valer — quem
     * conduz agora é a notificação.
     */
    const handleConfirmNotificacao = async () => {
        if (!cobrancaCliente) return;
        const ok = await handleMudarStatus('notificacao_extrajudicial', {
            notificacao_encaminhada_em: new Date().toISOString(),
            notificacao_encaminhada_por_id: user?.id || null,
            notificacao_encaminhada_por_nome: profile?.full_name || user?.email || 'Sistema',
            proximo_contato_em: null,
            ...(notificarPara ? { responsavel_cobranca_id: notificarPara } : {}),
        });
        if (!ok) return;
        setShowNotificacaoConfirmation(false);

        // O sino avisa quem vai notificar — a menos que seja quem clicou.
        if (!notificarPara || notificarPara === user?.id) return;
        try {
            await supabase.from('notifications').insert({
                user_id: notificarPara,
                type: 'notificacao_encaminhada',
                title: 'Cliente encaminhado para notificação extrajudicial',
                content: `${profile?.full_name || 'Um colega'} encaminhou "${cobrancaCliente.cliente_nome || clienteNome || cobrancaCliente.cpf_cnpj_cliente}" para a notificação extrajudicial.`,
                link: '/financeiro/esteira?fila=notificacao_extrajudicial',
                metadata: { cobranca_cliente_id: cobrancaCliente.id, cpf_cnpj_cliente: cobrancaCliente.cpf_cnpj_cliente },
            });
        } catch (error) {
            // O encaminhamento já valeu; o card aparece no painel de quem notifica.
            console.error('Encaminhado, mas o aviso no sino falhou:', error);
        }
    };

    const handleMarcarSemPagamento = async () => {
        await handleMudarStatus('sem_pagamento');
    };

    const handleConfirmNegativacaoRealizada = async () => {
        await handleMudarStatus('negativacao_realizada', {
            negativacao_realizada_em: new Date().toISOString(),
            negativacao_realizada_por_id: user?.id || null,
            negativacao_realizada_por_nome: profile?.full_name || user?.email || 'Sistema',
            // Negativado fica com quem negativou: é quem acompanha e quem
            // retira a negativação quando o cliente regularizar.
            ...(user?.id ? { responsavel_cobranca_id: user.id } : {}),
        });
        setShowNegativacaoRealizadaConfirmation(false);
    };

    const handleRegistrarRetirada = async () => {
        if (!cobrancaCliente) return;
        const confirmado = window.confirm('Confirma que a negativação já foi retirada do SPC/Serasa?');
        if (!confirmado) return;
        setRegistrandoRetirada(true);
        try {
            const nome = profile?.full_name || user?.email || 'Sistema';
            await registrarRetiradaNegativacao(cobrancaCliente.id, cobrancaCliente.status_negociacao, user?.id || '', nome);
            setCobrancaCliente({
                ...cobrancaCliente,
                negativacao_retirada_em: new Date().toISOString(),
                negativacao_retirada_por_id: user?.id || null,
                negativacao_retirada_por_nome: nome,
            });
            await recarregarTimeline();
            addToast('Retirada da negativação registrada.', 'success');
        } catch (error: any) {
            addToast(error.message || 'Erro ao registrar a retirada.', 'error');
        } finally {
            setRegistrandoRetirada(false);
        }
    };

    const handleAdicionarObservacao = async () => {
        if (!cobrancaCliente || !novaObservacao.trim()) return;
        setStatusUpdating(true);
        try {
            await adicionarObservacaoCobranca(
                cobrancaCliente.id,
                user?.id || '',
                profile?.full_name || user?.email || 'Sistema',
                novaObservacao.trim(),
                cobrancaCliente.status_negociacao
            );
            setNovaObservacao('');
            await recarregarTimeline();
        } catch (error: any) {
            addToast(error.message || 'Erro ao registrar observação.', 'error');
        } finally {
            setStatusUpdating(false);
        }
    };

    /**
     * Registro do atendimento: data do contato, observação e como o cliente
     * ficou — retorno ou status (#6147, #6165). Vai pela função do banco, que
     * também fecha as etapas de contato da régua deste cliente para todo mundo
     * e, se ninguém tinha o cliente, passa a carteira para quem atendeu.
     */
    const handleRegistrarContato = async () => {
        if (!cobrancaCliente || !user) return;
        const falta = faltaNoDesfecho(contatoDesfecho, diaLocalISO());
        if (falta) {
            addToast(falta, 'error');
            return;
        }
        setRegistrandoContato(true);
        try {
            const atualizado = await registrarContatoCliente(cobrancaCliente.cpf_cnpj_cliente, cobrancaCliente.cliente_nome || clienteNome || null, {
                canal: contatoCanal,
                data: contatoData || diaLocalISO(),
                resultado: contatoResultado,
                observacao: contatoObs,
                desfecho: contatoDesfecho,
            }, { id: user.id, nome: profile?.full_name || user.email || 'Sistema' });
            setCobrancaCliente(atualizado);
            if (!cobrancaCliente.responsavel_cobranca_id) {
                setResponsavelNome(profile?.full_name || user.email || 'Você');
            }
            setProximoContato(atualizado.proximo_contato_em || '');
            setContatoObs('');
            setContatoData('');
            setShowContatoForm(false);
            await recarregarTimeline();
            addToast(`Contato registrado. ${avisoDoDesfecho(contatoDesfecho) ?? ''}`.trim(), 'success');
        } catch (error: any) {
            addToast(error.message || 'Erro ao registrar contato.', 'error');
        } finally {
            setRegistrandoContato(false);
        }
    };

    const handleSalvarProximoContato = async (data: string) => {
        if (!cobrancaCliente) return;
        setProximoContato(data);
        try {
            const { error } = await supabase
                .from('cobranca_clientes')
                .update({ proximo_contato_em: data || null })
                .eq('id', cobrancaCliente.id);
            if (error) throw error;
            setCobrancaCliente({ ...cobrancaCliente, proximo_contato_em: data || null });
            addToast('Próximo contato agendado.', 'success');
        } catch (error: any) {
            addToast(error.message || 'Erro ao agendar próximo contato.', 'error');
        }
    };

    const handleConfirmTransferir = async () => {
        if (!cobrancaCliente || !transferTargetId) return;
        setTransferring(true);
        try {
            const responsavelAnterior = cobrancaCliente.responsavel_cobranca_id;
            await transferirCobrancaCliente(cobrancaCliente.id, transferTargetId);

            await logAction(user?.id || 'SYSTEM', 'TRANSFER_COBRANCA_CLIENTE', {
                cobranca_cliente_id: cobrancaCliente.id,
                cpf_cnpj_cliente: cobrancaCliente.cpf_cnpj_cliente,
                de: responsavelAnterior,
                para: transferTargetId,
            });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'inadimplencia', acao: 'transferir_cobranca_cliente', origem: 'acao' });

            const { data: prefs } = await supabase
                .from('notification_preferences')
                .select('cobranca_followup, push_enabled')
                .eq('user_id', transferTargetId)
                .maybeSingle();
            if (!prefs || (prefs.push_enabled !== false && prefs.cobranca_followup !== false)) {
                await supabase.from('notifications').insert({
                    user_id: transferTargetId,
                    type: 'cobranca_followup',
                    title: 'Cliente de cobrança transferido para você',
                    content: `${profile?.full_name || 'Um colega'} transferiu "${cobrancaCliente.cliente_nome || cobrancaCliente.cpf_cnpj_cliente}" para a sua carteira.`,
                    link: '/financeiro/faturas',
                    metadata: { cobranca_cliente_id: cobrancaCliente.id, cpf_cnpj_cliente: cobrancaCliente.cpf_cnpj_cliente },
                });
            }

            setCobrancaCliente({ ...cobrancaCliente, responsavel_cobranca_id: transferTargetId });
            setResponsavelNome(analistasCobranca.find(a => a.id === transferTargetId)?.full_name || null);
            addToast('Cliente transferido com sucesso.', 'success');
            setShowTransferModal(false);
            setTransferTargetId('');
        } catch (error: any) {
            addToast(error.message || 'Erro ao transferir cliente.', 'error');
        } finally {
            setTransferring(false);
        }
    };

    const handleAssumirResponsabilidade = async () => {
        if (!cobrancaCliente || !user) return;
        setAssumindo(true);
        try {
            await transferirCobrancaCliente(cobrancaCliente.id, user.id);
            await logAction(user.id, 'ASSUMIR_COBRANCA_CLIENTE', {
                cobranca_cliente_id: cobrancaCliente.id,
                cpf_cnpj_cliente: cobrancaCliente.cpf_cnpj_cliente,
            });
            logUsageEvent({ userId: user.id, modulo: 'crm', funcionalidade: 'inadimplencia', acao: 'assumir_cobranca_cliente', origem: 'acao' });
            setCobrancaCliente({ ...cobrancaCliente, responsavel_cobranca_id: user.id });
            setResponsavelNome(profile?.full_name || user.email || 'Você');
            addToast('Você agora é responsável por esta negociação.', 'success');
            setShowAssumirResponsabilidade(false);
        } catch (error: any) {
            addToast(error.message || 'Erro ao assumir a responsabilidade.', 'error');
        } finally {
            setAssumindo(false);
        }
    };

    if (!podeVerPainelCobranca) return null;

    return (
        <>
            {/* Assumir Responsabilidade — aparece automaticamente ao abrir um cliente sem responsável */}
            {showAssumirResponsabilidade && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-primary/10 dark:bg-primary/20 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
                            <UserCheck size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            {cobrancaCliente?.responsavel_cobranca_id ? 'Assumir este cliente?' : 'Você será responsável por esta negociação?'}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                            {cobrancaCliente?.responsavel_cobranca_id
                                ? `${clienteNome || 'Este cliente'} já está sendo atendido por ${responsavelNome || 'outro analista'}. Deseja assumir mesmo assim?`
                                : `${clienteNome || 'Este cliente'} ainda não tem um analista responsável na carteira de cobrança.`}
                        </p>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setShowAssumirResponsabilidade(false)}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Agora não
                            </button>
                            <button
                                onClick={handleAssumirResponsabilidade}
                                disabled={assumindo}
                                className="px-6 py-2.5 rounded-xl bg-primary text-white font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {assumindo ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
                                {assumindo ? 'Salvando...' : 'Sim, assumir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Marcar como Negociado */}
            {showNegociarForm && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <BadgeCheck size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Marcar como Negociado</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                            Informe o valor e a data combinados com o cliente.
                        </p>

                        <div className="mb-4 text-left">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Valor negociado (R$) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={negociarValor}
                                onChange={(e) => setNegociarValor(e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>
                        <div className="mb-6 text-left">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Data combinada para pagamento <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={negociarData}
                                onChange={(e) => setNegociarData(e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => { setShowNegociarForm(false); setNegociarValor(''); setNegociarData(''); }}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleConfirmNegociado}
                                disabled={statusUpdating}
                                className="px-6 py-2.5 rounded-xl border-2 border-indigo-500 text-indigo-600 font-bold hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors disabled:opacity-50"
                            >
                                {statusUpdating ? 'Salvando...' : 'Confirmar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Marcar como Pago */}
            {showPagoForm && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CircleDollarSign size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Marcar como Pago</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                            Confirme só depois de conferir o comprovante. Fica registrado que <strong>{profile?.full_name || user?.email || 'você'}</strong> efetivou este recebimento.
                        </p>

                        <div className="mb-4 text-left">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Data do pagamento <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={pagoData}
                                onChange={(e) => setPagoData(e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>
                        <div className="mb-4 text-left">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Valor recebido (R$)
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={pagoValor}
                                onChange={(e) => setPagoValor(e.target.value)}
                                placeholder="Opcional — pode diferir do valor negociado"
                                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>
                        <div className="mb-6 text-left">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Observação
                            </label>
                            <input
                                type="text"
                                value={pagoObs}
                                onChange={(e) => setPagoObs(e.target.value)}
                                placeholder="Ex.: comprovante PIX enviado no WhatsApp"
                                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => { setShowPagoForm(false); setPagoValor(''); setPagoData(''); setPagoObs(''); }}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleConfirmPago}
                                disabled={statusUpdating}
                                className="px-6 py-2.5 rounded-xl border-2 border-emerald-500 text-emerald-600 font-bold hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-colors disabled:opacity-50"
                            >
                                {statusUpdating ? 'Salvando...' : 'Confirmar recebimento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Negativar (SPC/Serasa) Confirmation */}
            {showNegativarConfirmation && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <ShieldAlert size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Marcar como Negativar?</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                            Isso só registra internamente que este cliente está apto/marcado para inclusão em SPC/Serasa (data e responsável). Não envia nada automaticamente a nenhum serviço externo — a inclusão em si continua sendo feita manualmente por quem for responsável.
                        </p>

                        <div className="mb-6 text-left">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">
                                Quem vai negativar
                            </label>
                            <select
                                value={negativarPara}
                                onChange={(e) => setNegativarPara(e.target.value)}
                                className="w-full bg-gray-100 dark:bg-gray-700 border-none rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500"
                            >
                                <option value="">Manter com {responsavelNome || 'o responsável atual'}</option>
                                {analistasCobranca.map(a => (
                                    <option key={a.id} value={a.id}>{a.full_name}</option>
                                ))}
                            </select>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                                O cliente passa para essa pessoa e sai da fila de contato de quem atendia.
                            </p>
                        </div>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setShowNegativarConfirmation(false)}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleConfirmNegativar}
                                disabled={statusUpdating}
                                className="px-6 py-2.5 rounded-xl border-2 border-red-500 text-red-600 font-bold hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors disabled:opacity-50"
                            >
                                {statusUpdating ? 'Salvando...' : 'Sim, marcar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Encaminhar para a notificação extrajudicial */}
            {showNotificacaoConfirmation && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 text-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FileWarning size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Encaminhar para notificação extrajudicial?</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                            O cliente sai da fila de contato e passa para quem faz a notificação. O retorno agendado deixa de valer.
                        </p>

                        <div className="mb-6 text-left">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">
                                Quem faz a notificação
                            </label>
                            <select
                                value={notificarPara}
                                onChange={(e) => setNotificarPara(e.target.value)}
                                className="w-full bg-gray-100 dark:bg-gray-700 border-none rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                <option value="">Manter com {responsavelNome || 'o responsável atual'}</option>
                                {analistasCobranca.map(a => (
                                    <option key={a.id} value={a.id}>{a.full_name}</option>
                                ))}
                            </select>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                                Aparece no Painel de Cobrança dessa pessoa, com o botão para gerar a notificação.
                            </p>
                        </div>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setShowNotificacaoConfirmation(false)}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleConfirmNotificacao}
                                disabled={statusUpdating}
                                className="px-6 py-2.5 rounded-xl border-2 border-orange-500 text-orange-600 font-bold hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors disabled:opacity-50"
                            >
                                {statusUpdating ? 'Salvando...' : 'Sim, encaminhar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmar Negativação Realizada */}
            {showNegativacaoRealizadaConfirmation && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <ShieldAlert size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Confirmar Negativação Realizada?</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                            Marque isto só depois que a inclusão no SPC/Serasa tiver sido de fato feita externamente — é diferente de "Negativação pendente", que só registra a decisão.
                        </p>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setShowNegativacaoRealizadaConfirmation(false)}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleConfirmNegativacaoRealizada}
                                disabled={statusUpdating}
                                className="px-6 py-2.5 rounded-xl border-2 border-red-500 text-red-600 font-bold hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors disabled:opacity-50"
                            >
                                {statusUpdating ? 'Salvando...' : 'Sim, confirmar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transferir Cliente */}
            {showTransferModal && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 text-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Users size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Transferir Cliente</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                            Escolha o analista que passará a ser responsável por este cliente na carteira de cobrança.
                        </p>

                        <div className="mb-6 text-left">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1.5">
                                Transferir para
                            </label>
                            <select
                                value={transferTargetId}
                                onChange={(e) => setTransferTargetId(e.target.value)}
                                className="w-full bg-gray-100 dark:bg-gray-700 border-none rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="">Selecione um analista...</option>
                                {analistasCobranca
                                    .filter(a => a.id !== cobrancaCliente?.responsavel_cobranca_id)
                                    .map(analyst => (
                                        <option key={analyst.id} value={analyst.id}>{analyst.full_name}</option>
                                    ))}
                            </select>
                        </div>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => { setShowTransferModal(false); setTransferTargetId(''); }}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleConfirmTransferir}
                                disabled={transferring || !transferTargetId}
                                className="px-6 py-2.5 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {transferring ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
                                {transferring ? 'Transferindo...' : 'Confirmar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Painel principal */}
            {!loadingCobranca && cobrancaCliente && (
                <div className="w-full max-w-3xl bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5 border border-gray-100 dark:border-gray-700 mb-8 text-left">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${STATUS_NEGOCIACAO_META[cobrancaCliente.status_negociacao].classes}`}>
                                {STATUS_NEGOCIACAO_META[cobrancaCliente.status_negociacao].label}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Responsável: <strong>{responsavelNome || 'não atribuído'}</strong>
                            </span>
                        </div>
                        {canTransferirCliente() && (
                            <button
                                onClick={() => { setTransferTargetId(''); setShowTransferModal(true); }}
                                className="text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/10 hover:bg-purple-100 dark:hover:bg-purple-900/20 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 border border-purple-100 dark:border-purple-900/30"
                            >
                                <Users size={14} /> Transferir
                            </button>
                        )}
                    </div>

                    {cobrancaCliente.status_negociacao === 'negociado' && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                            Valor negociado: <strong>{(cobrancaCliente.valor_negociado || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                            {cobrancaCliente.data_pagamento_negociado && <> • combinado para {cobrancaCliente.data_pagamento_negociado.split('-').reverse().join('/')}</>}
                        </p>
                    )}
                    {cobrancaCliente.status_negociacao === 'pago' && cobrancaCliente.pago_em && (
                        <p className="text-sm text-emerald-700 dark:text-emerald-400 mb-3">
                            Recebimento confirmado em {new Date(cobrancaCliente.pago_em).toLocaleDateString('pt-BR')}
                            {cobrancaCliente.pago_por_nome && <> por <strong>{cobrancaCliente.pago_por_nome}</strong></>}
                            {cobrancaCliente.valor_pago != null && <> • {cobrancaCliente.valor_pago.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</>}
                        </p>
                    )}
                    {cobrancaCliente.status_negociacao === 'notificacao_extrajudicial' && cobrancaCliente.notificacao_encaminhada_em && (
                        <p className="text-sm text-orange-700 dark:text-orange-400 mb-3">
                            Encaminhado para notificação extrajudicial em {new Date(cobrancaCliente.notificacao_encaminhada_em).toLocaleDateString('pt-BR')}
                            {cobrancaCliente.notificacao_encaminhada_por_nome ? ` por ${cobrancaCliente.notificacao_encaminhada_por_nome}` : ''} — aguardando a notificação ser gerada.
                        </p>
                    )}
                    {cobrancaCliente.status_negociacao === 'negativacao_pendente' && cobrancaCliente.negativado_em && (
                        <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                            Marcado para negativar em {new Date(cobrancaCliente.negativado_em).toLocaleDateString('pt-BR')} por {cobrancaCliente.negativado_por_nome} — negativação ainda não confirmada como executada.
                        </p>
                    )}
                    {cobrancaCliente.status_negociacao === 'negativacao_realizada' && cobrancaCliente.negativacao_realizada_em && !cobrancaCliente.negativacao_retirada_em && (
                        <p className="text-sm text-red-700 dark:text-red-400 mb-3">
                            Negativação executada em {new Date(cobrancaCliente.negativacao_realizada_em).toLocaleDateString('pt-BR')} por {cobrancaCliente.negativacao_realizada_por_nome}
                        </p>
                    )}
                    {/* Negativado que regularizou: o nome só sai do SPC quando
                        alguém tira — e isso precisa ficar registrado (#6147). */}
                    {cobrancaCliente.negativacao_realizada_em && cobrancaCliente.status_negociacao !== 'negativacao_realizada' && !cobrancaCliente.negativacao_retirada_em && (
                        <div className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-lg p-3 mb-3">
                            Este cliente foi negativado em {new Date(cobrancaCliente.negativacao_realizada_em).toLocaleDateString('pt-BR')}
                            {cobrancaCliente.negativacao_realizada_por_nome ? ` por ${cobrancaCliente.negativacao_realizada_por_nome}` : ''} e a negativação ainda não foi retirada.
                        </div>
                    )}
                    {cobrancaCliente.negativacao_retirada_em && (
                        <p className="text-sm text-emerald-700 dark:text-emerald-400 mb-3">
                            Negativação retirada em {new Date(cobrancaCliente.negativacao_retirada_em).toLocaleDateString('pt-BR')}
                            {cobrancaCliente.negativacao_retirada_por_nome ? <> por <strong>{cobrancaCliente.negativacao_retirada_por_nome}</strong></> : null}
                        </p>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                        <button
                            disabled={statusUpdating || cobrancaCliente.status_negociacao === 'negociando'}
                            onClick={() => handleMudarStatus('negociando')}
                            className="px-3 py-2 rounded-lg text-xs font-bold bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400 border border-amber-100 dark:border-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/20 disabled:opacity-40 transition-colors"
                        >
                            Marcar como Negociando
                        </button>
                        <button
                            disabled={statusUpdating}
                            onClick={() => setShowNegociarForm(true)}
                            className="px-3 py-2 rounded-lg text-xs font-bold bg-indigo-50 dark:bg-indigo-900/10 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 disabled:opacity-40 transition-colors"
                        >
                            Marcar como Negociado
                        </button>
                        <button
                            disabled={statusUpdating || cobrancaCliente.status_negociacao === 'pago'}
                            onClick={() => {
                                // Pré-preenche com o acordo vigente e a data de hoje: o
                                // caso comum é o cliente pagar o combinado no dia em que
                                // o agente confere o comprovante. Ambos editáveis.
                                setPagoValor(cobrancaCliente.valor_negociado ? String(cobrancaCliente.valor_negociado) : '');
                                setPagoData(new Date().toLocaleDateString('sv-SE'));
                                setShowPagoForm(true);
                            }}
                            className="px-3 py-2 rounded-lg text-xs font-bold bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 disabled:opacity-40 transition-colors flex items-center gap-1.5"
                        >
                            <CircleDollarSign size={14} /> Marcar como Pago
                        </button>
                        <button
                            disabled={statusUpdating || cobrancaCliente.status_negociacao === 'sem_pagamento'}
                            onClick={handleMarcarSemPagamento}
                            className="px-3 py-2 rounded-lg text-xs font-bold bg-rose-50 dark:bg-rose-900/10 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-900/30 hover:bg-rose-100 dark:hover:bg-rose-900/20 disabled:opacity-40 transition-colors"
                        >
                            Marcar como Sem pagamento
                        </button>
                        <button
                            disabled={statusUpdating || ['notificacao_extrajudicial', 'notificacao_realizada', 'negativacao_pendente', 'negativacao_realizada'].includes(cobrancaCliente.status_negociacao)}
                            onClick={() => {
                                // Já vem marcado com quem notifica (Bruna), a não ser
                                // que o cliente já esteja com essa pessoa.
                                setNotificarPara(quemNotificaPadrao && quemNotificaPadrao !== cobrancaCliente.responsavel_cobranca_id ? quemNotificaPadrao : '');
                                setShowNotificacaoConfirmation(true);
                            }}
                            className="px-3 py-2 rounded-lg text-xs font-bold bg-orange-50 dark:bg-orange-900/10 text-orange-700 dark:text-orange-400 border border-orange-100 dark:border-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/20 disabled:opacity-40 transition-colors flex items-center gap-1.5"
                            title="Passa o cliente para quem faz a notificação extrajudicial e tira da fila de contato"
                        >
                            <FileWarning size={14} /> Notificação extrajudicial
                        </button>
                        <button
                            disabled={statusUpdating || cobrancaCliente.status_negociacao === 'negativacao_pendente' || cobrancaCliente.status_negociacao === 'negativacao_realizada'}
                            onClick={() => {
                                // Já vem marcado com quem negativa (Bruna), a não ser que
                                // o cliente já esteja com essa pessoa.
                                setNegativarPara(quemNegativaPadrao && quemNegativaPadrao !== cobrancaCliente.responsavel_cobranca_id ? quemNegativaPadrao : '');
                                setShowNegativarConfirmation(true);
                            }}
                            className="px-3 py-2 rounded-lg text-xs font-bold bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-400 border border-red-100 dark:border-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/20 disabled:opacity-40 transition-colors flex items-center gap-1.5"
                        >
                            <ShieldAlert size={14} /> Negativar (SPC/Serasa)
                        </button>
                        {cobrancaCliente.status_negociacao === 'negativacao_pendente' && (
                            <button
                                disabled={statusUpdating}
                                onClick={() => setShowNegativacaoRealizadaConfirmation(true)}
                                className="px-3 py-2 rounded-lg text-xs font-bold bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800/50 hover:bg-red-200 dark:hover:bg-red-900/30 disabled:opacity-40 transition-colors flex items-center gap-1.5"
                            >
                                <ShieldAlert size={14} /> Confirmar Negativação Realizada
                            </button>
                        )}
                        {cobrancaCliente.negativacao_realizada_em && !cobrancaCliente.negativacao_retirada_em && (
                            <button
                                disabled={registrandoRetirada}
                                onClick={handleRegistrarRetirada}
                                className="px-3 py-2 rounded-lg text-xs font-bold bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 disabled:opacity-40 transition-colors flex items-center gap-1.5"
                                title="Use quando o cliente regularizou e a negativação já foi tirada do SPC/Serasa"
                            >
                                {registrandoRetirada ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Registrar retirada da negativação
                            </button>
                        )}
                    </div>

                    <div className="mb-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <CalendarClock size={16} className="text-gray-400 shrink-0" />
                            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 shrink-0">Próximo contato:</label>
                            <input
                                type="date"
                                value={proximoContato || ''}
                                onChange={(e) => handleSalvarProximoContato(e.target.value)}
                                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5 ml-6">
                            Até essa data o cliente fica fora do Painel de Cobrança. No dia, ele volta para o responsável e o sino avisa.
                        </p>
                    </div>

                    <div className="flex gap-2 mb-4">
                        <input
                            type="text"
                            value={novaObservacao}
                            onChange={(e) => setNovaObservacao(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAdicionarObservacao(); }}
                            placeholder="Adicionar observação sobre o contato com o cliente..."
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                        />
                        <button
                            onClick={handleAdicionarObservacao}
                            disabled={statusUpdating || !novaObservacao.trim()}
                            className="px-3 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-40 transition-colors shrink-0"
                        >
                            <MessageSquarePlus size={16} />
                        </button>
                    </div>

                    {/* Registro de tentativa de contato.
                        A régua automática cobre D-1 a D+24; quem deve há mais de
                        180 dias não tem etapa nenhuma, e era aqui que faltava um
                        lugar para anotar "liguei e não atendeu". */}
                    <div className="mb-4">
                        {showContatoForm ? (
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700 space-y-2">
                                <div className="flex flex-wrap gap-2">
                                    <label className="flex-1 min-w-[130px]">
                                        <span className="block text-[10px] font-bold uppercase text-gray-400 mb-1">Canal</span>
                                        <select
                                            value={contatoCanal}
                                            onChange={(e) => setContatoCanal(e.target.value as CobrancaCanalContato)}
                                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                                        >
                                            {CANAIS.map(c => <option key={c.v} value={c.v}>{c.t}</option>)}
                                        </select>
                                    </label>
                                    <label className="flex-1 min-w-[130px]">
                                        <span className="block text-[10px] font-bold uppercase text-gray-400 mb-1">Resultado</span>
                                        <select
                                            value={contatoResultado}
                                            onChange={(e) => setContatoResultado(e.target.value as CobrancaResultadoContato)}
                                            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                                        >
                                            {RESULTADOS.map(r => <option key={r.v} value={r.v}>{r.t}</option>)}
                                        </select>
                                    </label>
                                </div>
                                {/* Pedido #6147: quando falou e o que foi tratado,
                                    separados. #6165: e como ficou — retorno ou status. */}
                                <label className="block">
                                    <span className="block text-[10px] font-bold uppercase text-gray-400 mb-1">Data do contato</span>
                                    <input
                                        type="date"
                                        value={contatoData || diaLocalISO()}
                                        max={diaLocalISO()}
                                        onChange={(e) => setContatoData(e.target.value)}
                                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </label>
                                <DesfechoContatoCampos
                                    valor={contatoDesfecho}
                                    onChange={setContatoDesfecho}
                                    hoje={diaLocalISO()}
                                    amanha={diaLocalISO(1)}
                                    inputClassName="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                                    labelClassName="block text-[10px] font-bold uppercase text-gray-400 mb-1"
                                />
                                <textarea
                                    rows={2}
                                    value={contatoObs}
                                    onChange={(e) => setContatoObs(e.target.value)}
                                    placeholder="Observações do atendimento: o que foi dito, com quem falou, o que ficou combinado..."
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                                />
                                <div className="flex gap-2 justify-end">
                                    <button
                                        onClick={() => setShowContatoForm(false)}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleRegistrarContato}
                                        disabled={registrandoContato || !!faltaNoDesfecho(contatoDesfecho, diaLocalISO())}
                                        className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary/90 disabled:opacity-40 transition-colors flex items-center gap-1.5"
                                    >
                                        {registrandoContato ? <Loader2 size={13} className="animate-spin" /> : <PhoneCall size={13} />}
                                        Registrar contato
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => {
                                    // Retorno já marcado e ainda no futuro vem preenchido:
                                    // registrar o contato sem mexer nele não o apaga.
                                    setContatoDesfecho(desfechoInicial(proximoContato, diaLocalISO()));
                                    setShowContatoForm(true);
                                }}
                                className="w-full px-3 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1.5"
                            >
                                <PhoneCall size={13} /> Registrar contato
                            </button>
                        )}
                    </div>

                    {timeline.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                                <History size={12} /> Histórico de cobrança
                                <span className="font-normal normal-case tracking-normal text-gray-400">
                                    · negociação, contatos, avisos, notificações
                                </span>
                            </div>
                            {timeline.map((ev, i) => {
                                const meta = FONTE_EVENTO_META[ev.fonte];
                                return (
                                    <div key={`${ev.fonte}-${ev.em}-${i}`} className="text-xs bg-white dark:bg-gray-800 rounded-lg p-2.5 border border-gray-100 dark:border-gray-700">
                                        <div className="flex justify-between items-center gap-2 text-gray-400 dark:text-gray-500 mb-1">
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0 ${meta?.classes || ''}`}>
                                                {meta?.label || ev.fonte}
                                            </span>
                                            <span className="truncate">{ev.autor || ''}</span>
                                            <span className="shrink-0">
                                                {ev.em ? new Date(ev.em).toLocaleString('pt-BR') : '—'}
                                            </span>
                                        </div>
                                        <p className="text-gray-700 dark:text-gray-300 font-semibold break-words line-clamp-2">
                                            {ev.titulo}
                                        </p>
                                        {ev.detalhe ? (
                                            <p className="text-gray-600 dark:text-gray-400 mt-0.5 break-words">{ev.detalhe}</p>
                                        ) : null}
                                        {ev.url ? (
                                            <a
                                                href={ev.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary hover:underline mt-0.5 inline-block font-semibold"
                                            >
                                                Abrir documento
                                            </a>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400 dark:text-gray-500 italic text-center py-3">
                            Nenhum registro de cobrança para este cliente ainda.
                        </p>
                    )}
                </div>
            )}
        </>
    );
};

export default PainelNegociacaoCobranca;
